// src/utils/subcontractorUtils.js

/**
 * Subcontractor Utilities for Fason Management
 */

/**
 * Calculate subcontracting cost based on tariff and quantity
 * @param {Object} subcontractor - Subcontractor with cost tariff
 * @param {string} unit - Unit of measurement
 * @param {number} quantity - Quantity of items
 * @param {number} weight - Weight in kg (if applicable)
 * @returns {number} Calculated cost
 */
export const calculateSubcontractingCost = (subcontractor, unit, quantity, weight = 0) => {
    if (!subcontractor.costTariff || subcontractor.costTariff.length === 0) {
        return 0;
    }

    // Find matching cost tariff
    const costItem = subcontractor.costTariff.find(item => {
        if (item.type === 'per_unit') return item.unit === unit;
        if (item.type === 'per_kg') return item.unit === 'Kg';
        return item.type === 'per_job';
    });

    if (!costItem) {
        return 0;
    }

    switch (costItem.type) {
        case 'per_unit':
            return costItem.value * quantity;
        case 'per_kg':
            return costItem.value * (weight || 1);
        case 'per_job':
            return costItem.value;
        default:
            return 0;
    }
};

/**
 * Generate subcontracting order number
 * @returns {string} Subcontracting order number
 */
export const generateSubcontractingOrderNumber = () => {
    const prefix = 'FAS';
    const date = new Date();
    const year = date.getFullYear().toString().substr(2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    return `${prefix}${year}${month}${day}${random}`;
};

/**
 * Validate subcontractor data
 * @param {Object} subcontractor - Subcontractor data to validate
 * @returns {Object} Validation result
 */
export const validateSubcontractor = (subcontractor) => {
    const errors = [];
    
    if (!subcontractor.name) {
        errors.push('Firma adı boş olamaz');
    }
    
    if (!subcontractor.productionTypes || subcontractor.productionTypes.length === 0) {
        errors.push('En az bir üretim türü tanımlanmalıdır');
    }
    
    if (!subcontractor.costTariff || subcontractor.costTariff.length === 0) {
        errors.push('Maliyet tarifesi tanımlanmalıdır');
    }
    
    // Validate cost tariff
    subcontractor.costTariff.forEach((item, index) => {
        if (!item.type) {
            errors.push(`${index + 1}. maliyet kalemi için tür tanımlanmalıdır`);
        }
        
        if (item.value === undefined || item.value < 0) {
            errors.push(`${index + 1}. maliyet kalemi için geçerli bir değer girilmelidir`);
        }
        
        if (!item.unit) {
            errors.push(`${index + 1}. maliyet kalemi için birim tanımlanmalıdır`);
        }
        
        if (!item.currency) {
            errors.push(`${index + 1}. maliyet kalemi için para birimi tanımlanmalıdır`);
        }
    });
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Update subcontractor balance
 * @param {Object} subcontractor - Subcontractor object
 * @param {number} amount - Amount to add/subtract (negative for expenses, positive for payments)
 * @returns {number} New balance
 */
export const updateSubcontractorBalance = (subcontractor, amount) => {
    const currentBalance = subcontractor.balance || 0;
    return parseFloat((currentBalance + amount).toFixed(2));
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (TRY, USD, EUR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currency
    }).format(amount);
};

/**
 * Get production type badge color
 * @param {string} type - Production type
 * @returns {Object} Background and text color
 */
export const getProductionTypeColor = (type) => {
    const colors = {
        'Boyama': { bg: '#dbeafe', text: '#1e40af' },
        'Dikim': { bg: '#dcfce7', text: '#166534' },
        'Kesim': { bg: '#fef3c7', text: '#92400e' },
        'Pres': { bg: '#fce7f3', text: '#be185d' },
        'Baskı': { bg: '#ede9fe', text: '#7c3aed' },
        'Kalite Kontrol': { bg: '#cffafe', text: '#0891b2' }
    };
    
    return colors[type] || { bg: '#e2e8f0', text: '#374151' };
};

/**
 * Calculate payment statistics for subcontractor
 * @param {Array} orders - Subcontracting orders
 * @param {Array} payments - Payments made to subcontractor
 * @returns {Object} Payment statistics
 */
export const calculatePaymentStats = (orders, payments) => {
    const totalOrderCost = orders.reduce((sum, order) => sum + (order.cost || 0), 0);
    const totalPayments = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const outstandingBalance = totalOrderCost - totalPayments;
    
    return {
        totalOrderCost: parseFloat(totalOrderCost.toFixed(2)),
        totalPayments: parseFloat(totalPayments.toFixed(2)),
        outstandingBalance: parseFloat(outstandingBalance.toFixed(2))
    };
};

/**
 * Generate subcontractor performance report
 * @param {Array} orders - Subcontracting orders
 * @returns {Object} Performance metrics
 */
export const generatePerformanceReport = (orders) => {
    const totalOrders = orders.length;
    const completedOrders = orders.filter(order => order.status === 'Delivered').length;
    const inProgressOrders = orders.filter(order => order.status === 'In Progress').length;
    const delayedOrders = orders.filter(order => {
        if (order.status === 'Delivered' && order.actualDeliveryDate && order.expectedDeliveryDate) {
            return new Date(order.actualDeliveryDate) > new Date(order.expectedDeliveryDate);
        }
        if (order.status === 'In Progress' && order.expectedDeliveryDate) {
            return new Date() > new Date(order.expectedDeliveryDate);
        }
        return false;
    }).length;
    
    const onTimeDeliveryRate = totalOrders > 0 ? parseFloat(((completedOrders - delayedOrders) / totalOrders * 100).toFixed(2)) : 0;
    
    return {
        totalOrders,
        completedOrders,
        inProgressOrders,
        delayedOrders,
        onTimeDeliveryRate
    };
};

// Create a named export object instead of anonymous default export
const subcontractorUtils = {
    calculateSubcontractingCost,
    generateSubcontractingOrderNumber,
    validateSubcontractor,
    updateSubcontractorBalance,
    formatCurrency,
    getProductionTypeColor,
    calculatePaymentStats,
    generatePerformanceReport
};

export default subcontractorUtils;